import moment from 'moment';
import { RRule } from 'rrule';

/**
 * Time before a scheduled meeting's start when the "Join" affordance appears in the pre-room.
 * Used globally by every scheduled-meeting Join-button visibility check so the same number
 * applies to organisers and non-organisers, recurring and non-recurring meetings.
 */
export const JOIN_PREROOM_THRESHOLD_MS = 15 * 60 * 1000;

export interface OccurrenceSource {
  // Nullable for instant rooms — parseAnchor returns null, callers get null window.
  scheduled_at: string | null;
  ends_at: string | null;
  is_recurring?: boolean;
  recurrence_rule?: string | null;
  current_occurrence_scheduled_at?: string | null;
  current_occurrence_ends_at?: string | null;
}

export interface OccurrenceWindow {
  startMs: number;
  endMs: number;
}

const parseAnchor = (source: OccurrenceSource): { startMs: number; endMs: number; duration: number } | null => {
  if (!source.scheduled_at || !source.ends_at) return null;
  const startMs = Date.parse(source.scheduled_at);
  const endMs = Date.parse(source.ends_at);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  return { startMs, endMs, duration: endMs - startMs };
};

const buildRRule = (source: OccurrenceSource, anchorStartMs: number): RRule | null => {
  if (!source.is_recurring || !source.recurrence_rule) return null;
  try {
    return RRule.fromString(
      `DTSTART:${moment(anchorStartMs).utc().format('YYYYMMDDTHHmmss')}Z\n${source.recurrence_rule}`
    );
  } catch {
    return null;
  }
};

export const getCurrentOrNextOccurrenceWindow = (
  source: OccurrenceSource,
  now: Date = new Date()
): OccurrenceWindow | null => {
  const anchor = parseAnchor(source);
  if (!anchor) return null;
  const nowMs = now.getTime();

  const beStartStr = source.current_occurrence_scheduled_at;
  const beEndStr = source.current_occurrence_ends_at;
  if (beStartStr && beEndStr) {
    const beStart = Date.parse(beStartStr);
    const beEnd = Date.parse(beEndStr);
    if (Number.isFinite(beStart) && Number.isFinite(beEnd) && beEnd > nowMs) {
      return { startMs: beStart, endMs: beEnd };
    }
  }

  if (!source.is_recurring || !source.recurrence_rule) {
    return { startMs: anchor.startMs, endMs: anchor.endMs };
  }

  const rule = buildRRule(source, anchor.startMs);
  if (!rule) {
    return { startMs: anchor.startMs, endMs: anchor.endMs };
  }

  const nextStart = rule.after(new Date(nowMs - anchor.duration), true);
  if (!nextStart) return null;
  return { startMs: nextStart.getTime(), endMs: nextStart.getTime() + anchor.duration };
};

export const getMostRecentPastOccurrenceWindow = (
  source: OccurrenceSource,
  now: Date = new Date()
): OccurrenceWindow | null => {
  const anchor = parseAnchor(source);
  if (!anchor) return null;
  const nowMs = now.getTime();

  if (!source.is_recurring || !source.recurrence_rule) {
    if (anchor.endMs <= nowMs) {
      return { startMs: anchor.startMs, endMs: anchor.endMs };
    }
    return null;
  }

  const rule = buildRRule(source, anchor.startMs);
  if (!rule) {
    if (anchor.endMs <= nowMs) {
      return { startMs: anchor.startMs, endMs: anchor.endMs };
    }
    return null;
  }

  const cutoff = new Date(nowMs - anchor.duration);
  const occurrences = rule.between(new Date(anchor.startMs), cutoff, true);
  if (occurrences.length === 0) {
    if (anchor.endMs <= nowMs) {
      return { startMs: anchor.startMs, endMs: anchor.endMs };
    }
    return null;
  }
  const last = occurrences[occurrences.length - 1];
  const startMs = last.getTime();
  if (startMs + anchor.duration > nowMs) return null;
  return { startMs, endMs: startMs + anchor.duration };
};

export const isInOccurrenceWindow = (source: OccurrenceSource, now: Date = new Date()): boolean => {
  const window = getCurrentOrNextOccurrenceWindow(source, now);
  if (!window) return false;
  const nowMs = now.getTime();
  return nowMs >= window.startMs && nowMs < window.endMs;
};
